import { PlayerWithRels } from '@statsbomb/parachute-types';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useEffect } from 'react';
import { addRecentVisitAtom, recentlyVisitedByUserAtom } from '@/atoms/home/recentlyVisited';
import { rawFilteredPlayerInfoAtom } from '@/atoms/queries/players';
import { selectedTeamInfoAtom } from '@/atoms/queries/teams';
import { unwrappedHudlUserIdAtom } from '@/atoms/queries/user';
import { Entity } from '@/types/entity';
import { RecentVisit } from '@/types/recentlyVisited';

type VisitedEntity = Pick<RecentVisit, 'id' | 'name' | 'subline'>;

// Id and name come from one record, so a visit never pairs a new id with the previous entity's name.
const visitedTeamAtom = unwrap(
  atom(async (get): Promise<VisitedEntity | undefined> => {
    const team = await get(selectedTeamInfoAtom);
    return team && { id: team.team_id, name: team.name };
  }),
);

const visitedPlayerAtom = unwrap(
  atom(async (get): Promise<VisitedEntity | undefined> => {
    const player = await get(rawFilteredPlayerInfoAtom);
    if (!player) return undefined;

    // Only the players list carries the current club, and the atom's union type doesn't narrow on `in`.
    const club = 'current_team' in player ? (player as PlayerWithRels).current_team : null;
    return { id: player.player_id, name: player.display_name, subline: club?.name };
  }),
);

const visitedEntityAtoms = { team: visitedTeamAtom, player: visitedPlayerAtom };

export const useRecordRecentVisit = (type: Entity) => {
  const hudlUserId = useAtomValue(unwrappedHudlUserIdAtom);
  const entity = useAtomValue(visitedEntityAtoms[type]);
  const recordVisit = useSetAtom(addRecentVisitAtom);
  // Subscribing keeps the history in sync between tabs.
  useAtomValue(recentlyVisitedByUserAtom);

  const id = entity?.id;
  const name = entity?.name;
  const subline = entity?.subline;

  useEffect(() => {
    if (hudlUserId && id && name) {
      recordVisit(hudlUserId, { type, id, name, subline, visitedAt: Date.now() });
    }
  }, [hudlUserId, type, id, name, subline]);
};
